import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail } from "lucide-react";
import { toast } from "sonner";
import { api, errText } from "@/lib/api";
import { EASE } from "@/components/Motion";
import { useSite } from "@/context/SiteContext";

const KNOWN = "marais_visitor";

export const rememberVisitor = (v) => {
  if (v?.email) localStorage.setItem(KNOWN, JSON.stringify({ name: v.name || "", email: v.email }));
};

export const knownVisitor = () => {
  try { return JSON.parse(localStorage.getItem(KNOWN) || "null"); } catch (e) { return null; }
};

export const welcomeToast = (r) => {
  if (r?.welcome_sent) toast.success(`A welcome note is on its way to ${r.email}`);
  else toast.success(`Welcome back${r?.name ? `, ${r.name.split(" ")[0]}` : ""}`);
};

export const GoogleReturn = () => {
  const location = useLocation();
  const nav = useNavigate();
  const { refresh } = useSite() || {};
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sessionId = new URLSearchParams(location.hash.slice(1)).get("session_id");
    const back = location.pathname || "/";
    (async () => {
      try {
        const r = await api.post("/visitor/google", { session_id: sessionId });
        rememberVisitor(r.data);
        welcomeToast(r.data);
      } catch (er) { toast.error(errText(er)); }
      await refresh?.();
      nav(back, { replace: true });
    })();
  }, [location, nav, refresh]);
  return <div className="min-h-[100svh] bg-[#0B1D14]" data-testid="google-return" />;
};

const GoogleMark = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
  </svg>
);

export const VisitorWelcome = ({ dest, onDone }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + dest;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const r = await api.post("/visitor/email", { name, email });
      rememberVisitor(r.data);
      welcomeToast(r.data);
      onDone();
    } catch (er) { setErr(errText(er)); }
    setBusy(false);
  };
  return (
    <motion.div className="relative z-40 w-full max-w-md text-center" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE, delay: 1.2 }} data-testid="visitor-welcome">
      <p className="font-mono-x text-[11px] tracking-[0.3em] text-[#C9A24B]">YOU ARE IN</p>
      <h2 className="mt-4 font-display text-3xl sm:text-4xl text-[#F6EFE0]">Who is visiting?</h2>
      <p className="mt-3 text-sm text-[#F6EFE0]/85">Sign in so the family knows you stopped by, and we will send you a little welcome note.</p>
      <button onClick={google} className="mt-8 w-full inline-flex items-center justify-center gap-3 rounded-full bg-[#F6EFE0] text-[#0B1D14] py-3.5 font-medium transition-transform hover:-translate-y-0.5" data-testid="visitor-google-btn">
        <GoogleMark /> Continue with Google
      </button>
      <div className="my-6 flex items-center gap-4 text-xs text-[#F6EFE0]/70"><span className="h-px flex-1 bg-[#C9A24B]/30" />or use your email<span className="h-px flex-1 bg-[#C9A24B]/30" /></div>
      <form onSubmit={submit} className="space-y-3" data-testid="visitor-email-form">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="field !rounded-full !py-3.5 !px-6" data-testid="visitor-name-input" aria-label="Your name" />
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="field !rounded-full !py-3.5 !px-6" data-testid="visitor-email-input" aria-label="Your email" />
        <button className="btn-gold w-full justify-center" disabled={busy || !name || !email} data-testid="visitor-email-submit"><Mail size={16} /> {busy ? "Sending..." : "Send my welcome note"}</button>
      </form>
      {err && <p className="mt-4 text-sm text-[#F2B8A0]" role="alert" data-testid="visitor-error">{err}</p>}
      <button onClick={onDone} className="mt-6 text-sm text-[#E4C87F] underline underline-offset-4 hover:text-[#F6EFE0]" data-testid="visitor-skip-btn">Skip for now</button>
    </motion.div>
  );
};
